// ADR-099 follow-up — Architecture D: post-hoc citation chip placement via
// SENTINEL INJECTION into the answer string BEFORE markdown parsing.
//
// The prior approach matched a backend code-point offset (answer_span_end)
// onto a hast text node AFTER parsing, and SILENTLY DROPPED the chip when the
// offset landed at a markdown structural boundary (a sentence ending right
// before a bullet / GFM table / heading / \n\n break): the boundary char (\n
// or |) is consumed by remark-parse into structure and belongs to NO text
// node. Architecture D injects a parser-inert PUA sentinel
// `\uE000{spanArrayIndex}\uE001` into the STRING at the offset, so remark
// places it natively in the correct text node — string-adjacency becomes
// inline-adjacency, which works inside bullets / table cells / headings /
// strong / em. This is exactly what Google's Gemini groundingSupports
// rendering does for the identical offset-into-string problem (the rehype
// plugin then splits text nodes on the sentinel — see rehypeAttributionChips).
//
// Snap rules below are the Oracle binding pre-impl review
// (ses_159fb7ac1ffe2834t7fz9g7mAM): trailing-ws snap prevents the \n\n
// paragraph-merge; unclosed code regions during streaming are DROPPED (they
// reappear on the frame the region closes); structural-only lines are DROPPED;
// table-pipe + ATX-closing-hash are guarded; NO snap-forward (it would
// misattribute the chip to the next block).

import type { AttributionSpan } from "./rehypeAttributionChips";

export const SENTINEL_OPEN = "\uE000";
export const SENTINEL_CLOSE = "\uE001";

interface CodeRegion {
  start: number;
  end: number; // exclusive; for fenced = after the closing-fence line's newline
  closed: boolean;
}

// Detect inline-code spans + fenced code blocks (incl. an UNCLOSED fence/backtick
// during streaming, marked closed=false with end=content.length).
//
// Exported (with computeInsertPos) so the unverified-claim marker machinery
// (runtime/unverifiedMarks.ts) reuses the SAME hardened markdown-boundary logic
// — one coordinate space, one set of snap rules — instead of forking it.
export function detectCodeRegions(content: string): CodeRegion[] {
  const regions: CodeRegion[] = [];
  const n = content.length;
  const fenceRe = /^(\s{0,3})(`{3,}|~{3,})/;

  // Pass 1 — fenced blocks (line-based).
  const fenced: Array<[number, number, boolean]> = [];
  let openFence: { char: string; len: number; start: number } | null = null;
  let lineStart = 0;
  while (lineStart <= n) {
    let lineEnd = content.indexOf("\n", lineStart);
    const hasNl = lineEnd !== -1;
    if (lineEnd === -1) lineEnd = n;
    const line = content.slice(lineStart, lineEnd);
    const m = fenceRe.exec(line);
    if (!openFence && m) {
      openFence = { char: m[2][0], len: m[2].length, start: lineStart };
    } else if (openFence && m && m[2][0] === openFence.char && m[2].length >= openFence.len) {
      fenced.push([openFence.start, hasNl ? lineEnd + 1 : lineEnd, true]);
      openFence = null;
    }
    if (lineEnd === n) break;
    lineStart = lineEnd + 1;
  }
  if (openFence) fenced.push([openFence.start, n, false]); // unclosed
  for (const [s, e, closed] of fenced) regions.push({ start: s, end: e, closed });

  const inFenced = (idx: number) => fenced.some(([s, e]) => idx >= s && idx < e);

  // Pass 2 — inline code (backtick runs) outside fenced blocks.
  let j = 0;
  while (j < n) {
    if (content[j] === "`" && !inFenced(j)) {
      let k = j;
      while (k < n && content[k] === "`") k++;
      const runLen = k - j;
      let p = k;
      let closeStart = -1;
      while (p < n) {
        if (content[p] === "`" && !inFenced(p)) {
          let q = p;
          while (q < n && content[q] === "`") q++;
          if (q - p === runLen) {
            closeStart = p;
            p = q;
            break;
          }
          p = q;
        } else {
          p++;
        }
      }
      if (closeStart !== -1) {
        regions.push({ start: j, end: closeStart + runLen, closed: true });
        j = closeStart + runLen;
      } else {
        regions.push({ start: j, end: n, closed: false }); // unclosed inline
        j = n;
      }
    } else {
      j++;
    }
  }
  return regions;
}

function regionContaining(regions: CodeRegion[], pos: number): CodeRegion | null {
  for (const r of regions) {
    if (pos > r.start && pos < r.end) return r;
  }
  return null;
}

function getLine(content: string, pos: number): { start: number; text: string } {
  const start = content.lastIndexOf("\n", pos - 1) + 1;
  let end = content.indexOf("\n", pos);
  if (end === -1) end = content.length;
  return { start, text: content.slice(start, end) };
}

// Lines that are PURE markdown structure (no rendered text node) — never a safe
// sentinel anchor: GFM table delimiter row, setext underline / thematic break,
// marker-only list/blockquote/heading.
function isStructuralOnlyLine(line: string): boolean {
  const t = line.trim();
  if (t === "") return false;
  if (/^[|:\-\s]+$/.test(t) && t.includes("-")) return true; // table delimiter / setext-dash / hr-dash
  if (/^=+$/.test(t)) return true; // setext underline
  if (/^(\*\s*){3,}$/.test(t) || /^(_\s*){3,}$/.test(t)) return true; // thematic break
  if (/^#{1,6}$/.test(t)) return true; // marker-only heading
  if (/^[-*+]$/.test(t)) return true; // marker-only bullet
  if (/^\d+\.$/.test(t)) return true; // marker-only ordered item
  if (/^>+$/.test(t)) return true; // marker-only blockquote
  return false;
}

// `### Heading ###` — if the snap landed in the trailing closing-hash run,
// return the offset just after the heading TEXT (before the closing hashes).
function atxClosingHashSnap(pos: number, line: { start: number; text: string }): number | null {
  const m = /^(\s{0,3}#{1,6}\s+)(.*?)(\s+#+\s*)$/.exec(line.text);
  if (!m) return null;
  const textEnd = line.start + m[1].length + m[2].length;
  return pos > textEnd ? textEnd : null;
}

export function computeInsertPos(
  content: string,
  utf16End: number,
  codeRegions: CodeRegion[],
): { pos: number; drop: boolean } {
  let pos = utf16End;
  if (pos < 0) pos = 0;
  if (pos > content.length) pos = content.length;

  // (A) trailing-whitespace snap — land right after the last VISIBLE char.
  while (pos > 0 && /\s/.test(content[pos - 1])) pos--;

  // (B) code region.
  const region = regionContaining(codeRegions, pos);
  if (region) {
    if (!region.closed) return { pos, drop: true }; // unclosed (streaming) → drop this frame
    pos = region.end; // closed inline → after backtick; closed fenced → after fence line
  }

  // (C) structural-only line → drop.
  const line = getLine(content, pos);
  if (isStructuralOnlyLine(line.text)) return { pos, drop: true };

  // (C2) list-marker boundary → drop. The segmenter sometimes ends a span right
  // after a list-item marker (`3.` / `1)` / `-`); a sentinel between the marker
  // and its content de-parses the <li> to a paragraph AND chips the number.
  if (/^\s*(\d+[.)]|[-*+])\s*$/.test(content.slice(line.start, pos))) {
    return { pos, drop: true };
  }

  // (D) ATX closing-hash → snap before the closing hashes.
  const atx = atxClosingHashSnap(pos, line);
  if (atx !== null) pos = atx;

  // (E) table-pipe guard — never leave a sentinel after a cell pipe.
  if (pos > 0 && content[pos - 1] === "|") {
    pos--;
    while (pos > 0 && /[ \t]/.test(content[pos - 1])) pos--;
  }

  return { pos, drop: false };
}

// Inject `\uE000{spanArrayIndex}\uE001` at each span's (snapped) utf16End.
// Right-to-left insertion keeps earlier offsets valid; same-offset spans keep
// spans-array order. The rehype plugin later splits text nodes on the sentinel
// and resolves the encoded array index back to spans[i].chunkId / index.
export function insertCitationSentinels(content: string, spans: AttributionSpan[]): string {
  if (spans.length === 0) return content;
  // Collision guard: real corpus text never contains PUA sentinels, but if it
  // somehow did, injecting would corrupt the split — disable for this render.
  if (/\uE000\d+\uE001/.test(content)) return content;

  const codeRegions = detectCodeRegions(content);
  const inserts: Array<{ pos: number; ord: number }> = [];
  for (let ord = 0; ord < spans.length; ord++) {
    const res = computeInsertPos(content, spans[ord].utf16End, codeRegions);
    if (!res.drop) inserts.push({ pos: res.pos, ord });
  }
  // pos DESC (right-to-left), then ord DESC so that at a shared offset the
  // lower-ordinal span ends up FIRST in the string (matches spans-array order).
  inserts.sort((a, b) => b.pos - a.pos || b.ord - a.ord);

  let out = content;
  for (const { pos, ord } of inserts) {
    out = out.slice(0, pos) + SENTINEL_OPEN + String(ord) + SENTINEL_CLOSE + out.slice(pos);
  }
  return out;
}
